import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/hooks/use-auth";
import { useProfile } from "@/lib/useProfile";

export type SubscriptionTier = "free" | "premium" | "atelier";

export const FREE_LIMITS = {
  jewelry: 20,
  clothing: 20,
  looks: 7,
} as const;

export function useSubscription() {
  const { user } = useAuth();
  const uid = user?.id ?? null;
  const { data, isLoading } = useProfile();

  const tier: SubscriptionTier = (data?.subscription_tier ?? "free") as SubscriptionTier;
  const isFoundingMember = data?.founding_member === true;

  return {
    tier,
    isFree: tier === "free",
    isPremium: tier === "premium",
    isAtelier: tier === "atelier",
    isFoundingMember,
    isLoading: !!uid && isLoading,
  };
}

/**
 * Returns helpers that say whether a free-tier user can add another item of a
 * given kind. Premium and Atelier always bypass.
 */
export function useItemLimits() {
  const { user } = useAuth();
  const uid = user?.id ?? null;
  const sub = useSubscription();

  const { data: counts } = useQuery({
    queryKey: ["item_counts", uid],
    enabled: !!uid && sub.isFree,
    staleTime: 2 * 60 * 1000,
    queryFn: async () => {
      const [{ count: jewelry }, { count: clothing }, { count: looks }] = await Promise.all([
        supabase
          .from("jewelry_items")
          .select("id", { count: "exact", head: true })
          .eq("user_id", uid!)
          .eq("is_accessory", false)
          .eq("is_wishlist", false),
        supabase
          .from("clothing_items")
          .select("id", { count: "exact", head: true })
          .eq("user_id", uid!),
        supabase
          .from("saved_looks")
          .select("id", { count: "exact", head: true })
          .eq("user_id", uid!),
      ]);
      return {
        jewelry: jewelry ?? 0,
        clothing: clothing ?? 0,
        looks: looks ?? 0,
      };
    },
  });

  const bypass = !sub.isFree;

  return {
    ...sub,
    counts: counts ?? { jewelry: 0, clothing: 0, looks: 0 },
    canAddJewelry: bypass || (counts?.jewelry ?? 0) < FREE_LIMITS.jewelry,
    canAddClothing: bypass || (counts?.clothing ?? 0) < FREE_LIMITS.clothing,
    canAddLook: bypass || (counts?.looks ?? 0) < FREE_LIMITS.looks,
  };
}